import { Smartphone, BadgePercent, Wallet, Bell, Store, Apple, Play as PlayIcon } from "lucide-react";
import { SITE, CLUB_CATEGORIES } from "../data/site";
import { Overline, SectionTitle, Accent, Reveal } from "./Reveal";

const PhoneMockup = () => (
  <div className="relative mx-auto w-[290px] animate-float-slow" data-testid="app-phone-mockup">
    <div className="absolute -inset-6 rounded-[3rem] bg-[#00E676]/15 blur-3xl" />
    <div className="relative rounded-[2.6rem] border border-white/15 bg-[#04120E] p-2.5 shadow-2xl">
      <div className="rounded-[2rem] bg-[#0B261E] overflow-hidden">
        <div className="flex items-center justify-between px-5 pt-4 pb-2 text-[10px] text-emerald-100/60">
          <span className="font-mono2">9:41</span>
          <span className="w-16 h-4 rounded-full bg-[#04120E]" />
          <span>●●●</span>
        </div>
        <div className="px-5 pb-5 space-y-4">
          <div>
            <p className="text-[11px] text-emerald-100/55">Olá, bem-vindo</p>
            <p className="font-heading font-bold text-white text-base">Cliente iGreen</p>
          </div>
          <div className="rounded-2xl bg-[#00E676] p-4">
            <p className="text-[10px] font-semibold text-[#04120E]/70 uppercase tracking-wide">Total da sua economia</p>
            <p className="font-heading text-2xl font-extrabold text-[#04120E]">R$ 1.240,00</p>
            <p className="text-[11px] text-[#04120E]/70 mt-1">Seu cashback em conta <strong>R$ 86,00</strong></p>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {["Energia", "Telecom", "Seguro", "Descontos"].map((s) => (
              <div key={s} className="rounded-xl bg-[#04120E]/70 border border-white/10 py-2.5">
                <p className="text-[9px] text-emerald-100/65">{s}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl bg-[#04120E]/70 border border-white/10 p-3.5">
            <div className="flex justify-between items-center">
              <p className="text-[11px] font-semibold text-white">R$ 98,00</p>
              <span className="text-[9px] text-[#00E676] font-mono2">↓ 12% economia do mês</span>
            </div>
            <div className="mt-2.5 flex items-end gap-1.5 h-12">
              {[35, 55, 45, 70, 90].map((h, i) => (
                <div key={i} className="flex-1 rounded-t bg-[#00E676]/70" style={{ height: `${h}%` }} />
              ))}
            </div>
            <div className="flex justify-between text-[8px] text-emerald-100/40 mt-1">
              <span>Nov</span><span>Dez</span><span>Jan</span><span>Fev</span><span>Mar</span>
            </div>
          </div>
          <div className="rounded-xl bg-[#04120E]/70 border border-white/10 p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Bell size={13} className="text-[#A3E635]" />
              <div>
                <p className="text-[10px] font-semibold text-white">Fatura de energia</p>
                <p className="text-[9px] text-emerald-100/50">vence em 5 dias</p>
              </div>
            </div>
            <span className="text-[9px] font-bold text-[#04120E] bg-[#00E676] rounded-full px-2.5 py-1">Pagar</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const AppShowcase = () => (
  <section id="clube" className="relative py-24 sm:py-32 bg-[#04120E] overflow-hidden" data-testid="app-showcase-section">
    <div className="absolute top-20 -left-32 w-[420px] h-[420px] rounded-full bg-[#00E676]/8 blur-[120px]" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <Overline>Aplicativo do cliente</Overline>
        <SectionTitle>
          <Accent>Economia, cashback</Accent> e suas contas <Accent>em um só lugar</Accent>.
        </SectionTitle>
        <p className="mt-5 max-w-lg text-sm sm:text-base text-emerald-100/60 leading-relaxed">
          No app iGreen Club você acompanha a conta de energia, o celular e o seguro, indica amigos para ganhar cashback
          e aproveita o clube de descontos com mais de 600 mil ofertas.
        </p>
        <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-lg">
          {[
            { icon: BadgePercent, title: "Clube de descontos", text: "+600 mil ofertas em parceiros" },
            { icon: Wallet, title: "Cashback do mês", text: "+ R$ 25,00 em média" },
            { icon: Smartphone, title: "Tudo no app", text: "Energia, celular e seguro" },
          ].map((f) => (
            <div key={f.title} className="rounded-xl border border-white/10 bg-[#0B261E]/60 p-4">
              <f.icon size={18} className="text-[#00E676] mb-2.5" />
              <p className="text-xs font-bold text-white">{f.title}</p>
              <p className="text-[11px] text-emerald-100/50 mt-1 leading-snug">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={SITE.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="appstore-badge"
            className="inline-flex items-center gap-2.5 rounded-xl border border-white/15 bg-[#0B261E]/80 px-5 py-3 hover:border-[#00E676]/50 transition-colors duration-300"
          >
            <Apple size={22} className="text-white" />
            <span className="text-left leading-tight">
              <span className="block text-[9px] text-emerald-100/50">Disponível na</span>
              <span className="block text-sm font-bold text-white">App Store</span>
            </span>
          </a>
          <a
            href={SITE.googlePlayUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="googleplay-badge"
            className="inline-flex items-center gap-2.5 rounded-xl border border-white/15 bg-[#0B261E]/80 px-5 py-3 hover:border-[#00E676]/50 transition-colors duration-300"
          >
            <PlayIcon size={20} className="text-[#00E676]" />
            <span className="text-left leading-tight">
              <span className="block text-[9px] text-emerald-100/50">Disponível no</span>
              <span className="block text-sm font-bold text-white">Google Play</span>
            </span>
          </a>
        </div>
        <p className="mt-6 text-[11px] text-emerald-100/40 max-w-md leading-relaxed">
          Telas e valores ilustrativos do app iGreen Club. Ofertas e cupons dependem dos parceiros do clube.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <PhoneMockup />
      </Reveal>
    </div>

    {/* club categories marquee */}
    <div className="relative mt-20 border-y border-white/10 py-6 overflow-hidden" data-testid="club-marquee">
      <div className="animate-marquee-slow flex w-max items-center gap-4 whitespace-nowrap">
        {[...CLUB_CATEGORIES, ...CLUB_CATEGORIES].map((c, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/20 bg-[#0B261E]/70 px-5 py-2.5 text-sm font-medium text-emerald-100/70"
          >
            <Store size={13} className="text-[#00E676]" /> {c}
          </span>
        ))}
      </div>
    </div>
  </section>
);
